import isEmpty from 'lodash/isEmpty';
import { ReactElement, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/src/shared';
import { MessageType } from '@savgroup-front-common/types';

import { useConfirmationContext } from '../../../components/Confirmation/ConfirmationContextProvider';
import {
  removeAccent,
  safeFormattedIntlString,
  SafeFormattedMessageWithoutSpread,
} from '../../../formatters';
import { SUPPORTED_ICON_POSITIONS } from '../../../helpers';
import { ChevronDownIcon, PlusIcon } from '../../../protons/icons';
import { Menu, MenuItem } from '../../Menu';
import { MENU_ITEM_TYPES, MENU_POSITIONS } from '../../Menu/Menu.types';
import { SearchInput } from '../../SearchInput';
import Button from '../Button';

import { $MenuButtonContainer } from './MenuButton.styles';

export interface MenuOption<T = undefined> {
  value: string;
  label: string | MessageType;
  description?: string | MessageType;
  data?: T;
  SecondaryAction?: ReactElement;
  PrefixAction?: ReactElement;
  CustomLabel?: ReactElement;
  isActive?: boolean;
}

interface MenuButtonProps<T> {
  options: MenuOption<T>[];
  icon?: ReactElement;
  position?: MENU_POSITIONS;
  dataTestId?: string;
  disabled?: boolean;
  fluid?: boolean;
  isLoading?: boolean;
  secondary?: boolean;
  primary?: boolean;
  tertiary?: boolean;
  hollow?: boolean;
  underline?: boolean;
  naked?: boolean;
  small?: boolean;
  value: string;
  onChange: (option: MenuOption<T>) => void;
  onCreateNewOption?: () => void;
  createNewOptionLabel?: string | MessageType;
  searchPlaceHolderMessage?: string | MessageType;
  noResultsFoundMessage?: MessageType;
  title?: string | MessageType;
}

export function MenuButton<T = undefined>(
  props: MenuButtonProps<T>,
): ReactElement {
  const {
    options = [],
    icon = <ChevronDownIcon />,
    position = undefined,
    dataTestId = 'menu_button',
    disabled = false,
    fluid = false,
    isLoading = false,
    hollow = false,
    secondary = false,
    primary = false,
    tertiary = false,
    naked = false,
    underline = false,
    small = false,
    value,
    onChange,
    onCreateNewOption,
    createNewOptionLabel = '',
    searchPlaceHolderMessage,
    noResultsFoundMessage,
    title,
  } = props;
  const { refConfirmationModal } = useConfirmationContext();
  const [verticalMenuIsOpen, setVerticalMenuIsOpen] = useState(false);
  const refWrapper = useRef(null);
  const theme = useTheme();

  const currentMenuItem = options.find((option) => option.value === value);

  const [searchValue, setSearchValue] = useState<string>();

  const finalOptionList = useMemo(
    () =>
      onCreateNewOption
        ? [
            ...options.map((option) => {
              return {
                key: option.value,
                label: option.label,
                description: option.description,
                onClick: () => {
                  onChange(option);
                  setVerticalMenuIsOpen(false);
                },
                type: MENU_ITEM_TYPES.BUTTON,
                SecondaryAction: option.SecondaryAction,
                PrefixAction: option.PrefixAction,
                CustomLabel: option.CustomLabel,
                isActive: option.isActive,
              } as MenuItem;
            }),
            {
              key: 'CreateNewOption',
              label: createNewOptionLabel,
              color: theme.colors.primary,
              onClick: () => {
                setVerticalMenuIsOpen(false);
                onCreateNewOption();
              },
              icon: <PlusIcon />,
              type: MENU_ITEM_TYPES.BUTTON,
            } as MenuItem,
          ]
        : options.map((option) => {
            return {
              key: option.value,
              label: option.label,
              description: option.description,
              onClick: () => {
                onChange(option);
                setVerticalMenuIsOpen(false);
              },
              type: MENU_ITEM_TYPES.BUTTON,
              SecondaryAction: option.SecondaryAction,
              PrefixAction: option.PrefixAction,
              CustomLabel: option.CustomLabel,
              isActive: option.isActive,
            } as MenuItem;
          }),
    [
      onCreateNewOption,
      options,
      createNewOptionLabel,
      theme.colors.primary,
      onChange,
    ],
  );

  const finalOptions = useMemo(() => {
    return finalOptionList.filter((option) => {
      if ('label' in option) {
        return removeAccent(
          safeFormattedIntlString(option.label).toLowerCase(),
        ).includes(removeAccent(searchValue?.toLowerCase() ?? ''));
      }

      return false;
    });
  }, [finalOptionList, searchValue]);

  const portalRef = useRef(null);

  if (isEmpty(options)) {
    return <></>;
  }

  return (
    <$MenuButtonContainer
      ref={refWrapper}
      title={safeFormattedIntlString(title)}
    >
      <Button
        type={BUTTON_TYPES.BUTTON}
        isLoading={isLoading}
        onClick={(e) => {
          e.stopPropagation();
          setVerticalMenuIsOpen(!verticalMenuIsOpen);
        }}
        icon={icon}
        dataTestId={`menu_button_${dataTestId}`}
        disabled={disabled}
        fluid={fluid}
        hollow={hollow}
        underline={underline}
        secondary={secondary}
        tertiary={tertiary}
        primary={primary}
        naked={naked}
        small={small}
        position={SUPPORTED_ICON_POSITIONS.RIGHT}
      >
        {currentMenuItem && (
          <SafeFormattedMessageWithoutSpread message={currentMenuItem?.label} />
        )}
      </Button>
      <div id="filterViewDropdown" ref={portalRef} />

      <Menu
        menuItems={finalOptions}
        isOpen={verticalMenuIsOpen}
        onClose={() => {
          setVerticalMenuIsOpen(false);
        }}
        wrapperRefs={[refWrapper, refConfirmationModal, portalRef].filter(
          (x) => !!x,
        )}
        position={position}
        dataTestId={`menu_button_${dataTestId}`}
        noResultsFoundMessage={noResultsFoundMessage}
        minWidth={400}
      >
        <SearchInput
          onChange={(e) => {
            setSearchValue(e?.target?.value);
          }}
          onClear={() => setSearchValue('')}
          value={searchValue}
          placeholder={searchPlaceHolderMessage}
          shouldFocusOnMount
          isSmall
        />
      </Menu>
    </$MenuButtonContainer>
  );
}
